import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Fuse from "fuse.js";

import { PlusIcon, MinusIcon } from "@heroicons/react/20/solid";

import { GET, POST, PUT } from "../../../api/requests";
import Button from "../../../components/Button";
import NavBanner from "../../../components/Banners/NavBanner";
import { JSONEditor } from "./JSONEditor";
import { SimpleModal } from "../../../components/Modals/SimpleModal";
import { CredentialsModal } from "../../../components/Modals";
import { NodeDropdown } from "./NodeDropdown";
import { useDialog } from "../../../contexts/DialogContext";

export default function ManageWorkflow() {
  const { workflowId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialog();

  const [currentNodeIndex, setCurrentNodeIndex] = useState(null);
  const [objectFilterKeys, setObjectFilterKeys] = useState([]);
  // form state
  const [title, setTitle] = useState("");
  const [tenant, setTenant] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("");
  const [updateKey, setUpdateKey] = useState("");
  const [instance, setInstance] = useState("");
  const [sourceTable, setSourceTable] = useState("");
  const [nodes, setNodes] = useState([{ content: {} }]);
  const [trigger, setTrigger] = useState("{}");
  const [metadata, setMetadata] = useState("{}");
  const [platformCredentials, setPlatformCredentials] = useState([]);

  const [workflow, setWorkflow] = useState(state?.workflow);

  // search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // get the workflow
  const { data: workflowData } = useQuery(
    ["workflow", workflowId],
    async () => {
      if (!workflowId) return undefined;
      const response = await GET(`/workflow/${workflowId}`);
      return response.workflow;
    },
    {
      enabled: !!workflowId && !workflow,
      onSuccess: (data) => {
        if (data) setWorkflow(data);
      },
    }
  );

  // get all of the platform credentials
  const { data: credentialsData } = useQuery("platformCredentials", () =>
    GET("/platformCredentials")
  );

  const { mutate: createWorkflowMutation } = useMutation(
    (formData) => POST("/workflow", formData),
    {
      onSuccess: (data) => {
        const isScheduled = data.scheduledWorkflow;
        if (isScheduled) return navigate(`/automations/${data.workflow.id}`);
        navigate(-1);
      },
      onError: (error) => {
        // show error modal
        openDialog(
          <SimpleModal
            titleText="There was an error"
            bodyText={`Received this from the server: ${
              error.message || "unknown error"
            }`}
            variant="error"
          />
        );
      },
    }
  );

  const { mutate: updateWorkflowMutation } = useMutation(
    (formData) => PUT(`/workflow/${workflowId}`, formData),
    {
      onSuccess: () => {
        navigate(-1);
      },
      onError: (error) => {
        // show error modal
        openDialog(
          <SimpleModal
            titleText="There was an error"
            bodyText={`Received this from the server: ${
              error.message || "unknown error"
            }`}
            variant="error"
          />
        );
      },
    }
  );

  // fuse search instance
  const fuse = useMemo(() => {
    if (!platformCredentials.length) return null;
    return new Fuse(platformCredentials, {
      keys: ["tenant", "platform", "authType"],
      threshold: 0.3,
    });
  }, [platformCredentials]);

  useEffect(() => {
    if (trigger) {
      try {
        const parsed = JSON.parse(trigger);
        console.log(parsed.config?.mingoFilter);
        if (parsed.config?.mingoFilter || parsed.config?.objectLevelFilter) {
          setObjectFilterKeys(
            extractKeys(
              parsed.config?.mingoFilter || parsed.config?.objectLevelFilter
            )
          );
        }
      } catch (e) {
        console.log("Invalid JSON, not parsing");
      }
    }
  }, [trigger]);

  function extractKeys(obj) {
    const keys = new Set();

    function traverse(current) {
      if (typeof current !== "object" || current === null) return;

      if (Array.isArray(current)) {
        current.forEach(traverse);
      } else {
        for (const [key, value] of Object.entries(current)) {
          if (key === "$expr") {
            traverseExpr(value);
          } else if (!key.startsWith("$")) {
            keys.add(key);
          }
          traverse(value);
        }
      }
    }

    function traverseExpr(expr) {
      if (Array.isArray(expr)) {
        expr.forEach((item) => {
          if (typeof item === "string" && item.startsWith("$")) {
            keys.add(item.slice(1));
          }
        });
      } else if (typeof expr === "object" && expr !== null) {
        Object.values(expr).forEach(traverseExpr);
      }
    }

    traverse(obj);
    return Array.from(keys);
  }

  useEffect(() => {
    if (workflowData) {
      setTitle(workflowData.name || "");
      setTenant(workflowData.tenant || "");
      setTriggerEvent(workflowData.triggerEvent || "");
      setObjectFilterKeys(workflowData.objectFilterKeys || []);
      setUpdateKey(workflowData.updateKey || "");
      setInstance(workflowData.instance || "");
      setSourceTable(workflowData.sourceTable || "");
      setNodes(
        workflowData.nodes?.map((node) => ({ content: node })) || [
          { content: {} },
        ]
      );
      setTrigger(JSON.stringify(workflowData.trigger || {}, null, 2));
      setMetadata(JSON.stringify(workflowData.metadata || {}, null, 2));
    }
  }, [workflowData]);

  // platform credentials effect
  useEffect(() => {
    if (credentialsData) {
      setPlatformCredentials(credentialsData);
    }
  }, [credentialsData]);

  // fuse search effect
  useEffect(() => {
    if (!fuse || !searchTerm) {
      setSearchResults(platformCredentials);
      return;
    }

    const results = fuse.search(searchTerm);
    setSearchResults(results.map((result) => result.item));
  }, [searchTerm, fuse, platformCredentials]);

  const handleSave = async () => {
    const formData = {
      title,
      tenant,
      triggerEvent,
      objectFilterKeys,
      updateKey,
      instance,
      sourceTable,
      nodes: nodes.map((node) => node.content),
      trigger: JSON.parse(trigger),
      metadata: JSON.parse(metadata),
    };

    if (workflowId) return updateWorkflowMutation(formData);
    createWorkflowMutation(formData);
  };

  const addNode = () => setNodes([...nodes, { content: {} }]);
  const removeNode = () => nodes.length > 1 && setNodes(nodes.slice(0, -1));

  const updateNode = (index, value) => {
    setNodes((prevNodes) => {
      const updatedNodes = [...prevNodes];
      try {
        const parsedContent = JSON.parse(value);
        updatedNodes[index] = { content: parsedContent };
      } catch (err) {
        // if parsing fails, don't update the node
        console.error("Invalid JSON:", err);
      }
      return updatedNodes;
    });
  };

  const handleManageCredentials = (index) => {
    setCurrentNodeIndex(index);

    // open credentials modal
    openDialog(
      <CredentialsModal
        titleText="Select platform credentials"
        bodyText="Select the platform credentials you want to use for this node."
      >
        <div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search platform credentials"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
          />
        </div>
        <div
          className="mt-4 divide-y divide-gray-300 overflow-y-auto"
          style={{ maxHeight: "400px" }}
        >
          {searchResults.map((platform) => {
            const isSelected =
              currentNodeIndex !== null &&
              nodes[currentNodeIndex].content.platformCredential &&
              nodes[currentNodeIndex].content.platformCredential.includes(
                platform.id
              );

            return (
              <div key={platform.id} className="py-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handlePlatformChange(platform.id)}
                    className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-600 mr-4 ml-2"
                  />
                  <span className="font-medium text-gray-900">
                    Tenant: {platform.tenant} - platform: {platform.platform} -
                    auth type: {platform.authType}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      </CredentialsModal>,
      "max-w-2xl"
    );
  };

  const handlePlatformChange = (platformId) => {
    if (currentNodeIndex === null) return;

    setNodes((prevNodes) => {
      const updatedNodes = [...prevNodes];
      const currentNode = { ...updatedNodes[currentNodeIndex] };
      const currentCredential = currentNode.content.platformCredential || [];

      const updatedCredential = currentCredential.includes(platformId)
        ? currentCredential.filter((id) => id !== platformId)
        : [...currentCredential, platformId];

      currentNode.content.platformCredential = updatedCredential;
      updatedNodes[currentNodeIndex] = currentNode;

      return updatedNodes;
    });

    // close the dialog
    closeDialog();
  };

  return (
    <>
      <NavBanner />

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-12 lg:px-12">
        <h1
          className={`text-2xl font-bold text-gray-900 ${
            workflowId ? "mb-2" : "mb-8"
          }`}
        >
          {workflowId ? "Edit Workflow" : "Create Workflow"}
        </h1>

        {workflowId && (
          <p className="text-gray-500 mb-8">
            Note: currently, editing a workflow's schedule will not update the
            associated jobs. If you want to modify the schedule, please make a
            new workflow.
          </p>
        )}

        {/* title */}
        <div>
          <label
            htmlFor="title"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Title
          </label>
          <div className="mt-1 mb-8">
            <input
              id="title"
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Workflow title"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* tenant */}
        <div>
          <label
            htmlFor="tenant"
            className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
          >
            Tenant
          </label>
          <div className="mt-1 mb-8">
            <input
              id="tenant"
              name="tenant"
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="Tenant name"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* trigger event */}
        <div>
          <label
            htmlFor="triggerEvent"
            className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
          >
            Trigger event
          </label>
          <div className="mt-1 mb-8">
            <input
              id="triggerEvent"
              name="triggerEvent"
              type="text"
              value={triggerEvent}
              onChange={(e) => setTriggerEvent(e.target.value)}
              placeholder="Trigger event"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* update key */}
        <div>
          <label
            htmlFor="updateKey"
            className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
          >
            Update key
          </label>
          <div className="mt-1 mb-8">
            <input
              id="updateKey"
              name="updateKey"
              type="text"
              value={updateKey}
              onChange={(e) => setUpdateKey(e.target.value)}
              placeholder="Update key"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* source table and instance */}
        <div className="flex items-start space-x-4 mb-8">
          <div className="flex-1">
            <label
              htmlFor="table"
              className="block text-sm font-medium leading-6 text-gray-900 -mt-2"
            >
              Source table
            </label>
            <div className="mt-1">
              <select
                value={sourceTable}
                onChange={(e) => setSourceTable(e.target.value)}
                id="table"
                name="table"
                autoComplete="source-table-name"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              >
                <option>GENERIC_DATA</option>
                <option>PLATFORM_SAMSARA_TRACKING</option>
                <option>PLATFORM_PORTPRO_LOAD</option>
                <option>PLATFORM_PORTPRO_DOCUMENT</option>
                <option>PLATFORM_MCLEOD_LOAD</option>
                <option>PLATFORM_FLEXPORT_LOAD</option>
              </select>
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium leading-6 text-gray-900 -mt-2">
              Instance
            </label>
            <div className="mt-1">
              <input
                type="text"
                value={instance}
                onChange={(e) => setInstance(e.target.value)}
                placeholder="Instance"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        {/* nodes */}
        <div className="mb-6">
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
            Nodes
          </label>
          {nodes.map((node, index) => (
            <div key={index} className="mb-3">
              <div className="flex items-end justify-between mb-3">
                <div className="text-xs text-gray-500">Node {index + 1}</div>
                <NodeDropdown
                  onManageCredentials={() => handleManageCredentials(index)}
                />
              </div>
              <JSONEditor
                value={JSON.stringify(node.content, null, 2)}
                onChange={(value) => updateNode(index, value)}
                height="250"
              />
            </div>
          ))}
          <div className="flex justify-end space-x-2 mt-3">
            <Button
              label="Remove Node"
              onClick={removeNode}
              style={{ variant: "filled" }}
              rounded="md"
              variant="secondary"
              icon={<MinusIcon className="h-4 w-4" />}
              disabled={nodes.length === 1}
            />
            <Button
              label="Add Node"
              onClick={addNode}
              style={{ variant: "filled" }}
              rounded="md"
              icon={<PlusIcon className="h-4 w-4" />}
            />
          </div>
        </div>

        {/* trigger */}
        <JSONEditor
          label="Trigger"
          value={trigger}
          onChange={setTrigger}
          height="500"
        />

        <div className="mb-6">
          <h1 className="text-xs font-semibold text-slate-700 mb-2">
            Workflow will check for ANY the following keys to be updated before
            checking the filter
          </h1>
          <div className="flex flex-wrap">
            {objectFilterKeys.map((k) => (
              <span
                key={k}
                className="border mr-1.5 mb-1.5 border-slate-300 rounded-md py-1 px-2.5 text-xs"
              >
                {k}
              </span>
            ))}
          </div>
        </div>

        {/* Metadata */}
        <JSONEditor
          label="Metadata"
          value={metadata}
          onChange={setMetadata}
          height="300"
        />

        {/* Save and Cancel buttons */}
        <div className="flex items-center justify-end gap-x-3 pb-8 mt-4">
          <Button
            label="Go back"
            onClick={() => navigate(-1)}
            style={{ variant: "filled" }}
            rounded="md"
            variant="secondary"
          />
          <Button
            label="Save workflow"
            onClick={handleSave}
            style={{ variant: "filled" }}
            rounded="md"
            disabled={
              createWorkflowMutation.isLoading ||
              updateWorkflowMutation.isLoading
            }
          />
        </div>
      </div>
    </>
  );
}
